import { useEffect } from 'react';

type SeoParams = {
  title: string;
  description: string;
  keywords?: string;
};

const setMeta = (selector: string, attribute: 'name' | 'property', key: string, value: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  const previous = tag ? tag.getAttribute('content') : null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
  return () => {
    if (!tag) return;
    if (previous === null) {
      tag.remove();
    } else {
      tag.setAttribute('content', previous);
    }
  };
};

export default function useSeo({ title, description, keywords }: SeoParams) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const cleanups = [
      setMeta('meta[name="description"]', 'name', 'description', description),
      setMeta('meta[property="og:title"]', 'property', 'og:title', title),
      setMeta('meta[property="og:description"]', 'property', 'og:description', description),
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title),
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description),
    ];

    let cleanupKeywords: (() => void) | undefined;
    if (keywords) {
      cleanupKeywords = setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    }

    return () => {
      document.title = previousTitle;
      cleanups.forEach((fn) => fn());
      if (cleanupKeywords) cleanupKeywords();
    };
  }, [title, description, keywords]);
}